import { LitElement, html, svg } from 'lit';
import styles from './studio-theme-toggle.css.mjs';
import {
	THEME_MODES,
	THEME_CHANGE_EVENT,
	getThemeMode,
	getResolvedTheme,
	setThemeMode,
} from '../../styles/theme-mode.mjs';

/*
 * Icons are inlined rather than using <studio-icon>. The icon set is fetched
 * from the CDN at runtime and the built icons.json currently has no moon glyph,
 * so inlining keeps this component self-contained and avoids a network round
 * trip for chrome that renders on every page.
 */
const ICONS = {
	system: svg`<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-5v2h3a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2h3v-2H6a2 2 0 0 1-2-2V5Zm2 0v9h12V5H6Z"/></svg>`,
	light: svg`<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4a1 1 0 0 1-1-1V2a1 1 0 1 1 2 0v1a1 1 0 0 1-1 1Zm0 16a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Zm8-8a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2h-1a1 1 0 0 1-1-1ZM2 12a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Zm15.66-5.66a1 1 0 0 1 0-1.41l.7-.71a1 1 0 1 1 1.42 1.42l-.71.7a1 1 0 0 1-1.41 0ZM4.22 18.36a1 1 0 0 1 0-1.41l.7-.71a1 1 0 1 1 1.42 1.42l-.71.7a1 1 0 0 1-1.41 0Zm13.44 0a1 1 0 0 1 1.41 0l.71.7a1 1 0 1 1-1.42 1.42l-.7-.71a1 1 0 0 1 0-1.41ZM4.22 5.64a1 1 0 0 1 1.41 0l.71.7a1 1 0 1 1-1.42 1.42l-.7-.71a1 1 0 0 1 0-1.41ZM12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z"/></svg>`,
	dark: svg`<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12.3 2.04a1 1 0 0 1 .32 1.1 7 7 0 0 0 8.24 9.2 1 1 0 0 1 1.1 1.46A10 10 0 1 1 11.2 1.9a1 1 0 0 1 1.1.14Z"/></svg>`,
};

const LABELS = { system: 'System', light: 'Light', dark: 'Dark' };

/**
 * Three-state theme control: System / Light / Dark.
 *
 * "System" is the default and is stored as the absence of a preference, so the
 * page keeps following the OS if the user later changes it. Choosing Light or
 * Dark pins the theme and persists across reloads.
 */
export class StudioThemeToggle extends LitElement {
	static properties = {
		mode: { type: String, reflect: true },
		compact: { type: Boolean, reflect: true },
	};

	static styles = styles;

	constructor() {
		super();
		this.mode = getThemeMode();
		this.compact = false;
		this._onExternalChange = (e) => {
			const next = e?.detail?.mode;
			if (next && next !== this.mode) this.mode = next;
		};
		// Following the OS means re-rendering when the OS flips, so the
		// "System" button's title reflects what is actually being painted.
		this._media = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
		this._onMediaChange = () => {
			if (this.mode === 'system') this.requestUpdate();
		};
	}

	connectedCallback() {
		super.connectedCallback();
		// Another instance (or app code) may change the mode.
		globalThis.addEventListener?.(THEME_CHANGE_EVENT, this._onExternalChange);
		this._media?.addEventListener?.('change', this._onMediaChange);
		this.mode = getThemeMode();
	}

	disconnectedCallback() {
		globalThis.removeEventListener?.(THEME_CHANGE_EVENT, this._onExternalChange);
		this._media?.removeEventListener?.('change', this._onMediaChange);
		super.disconnectedCallback();
	}

	select(mode) {
		this.mode = setThemeMode(mode);
		this.dispatchEvent(new CustomEvent('theme-change', {
			detail: { mode: this.mode, resolved: getResolvedTheme(this.mode) },
			bubbles: true,
			composed: true,
		}));
	}

	render() {
		const resolved = getResolvedTheme(this.mode);
		return html`
			<div class="group" role="radiogroup" aria-label="Colour theme">
				${THEME_MODES.map((m) => html`
					<button
						type="button"
						role="radio"
						aria-checked=${this.mode === m ? 'true' : 'false'}
						title=${m === 'system'
		? `Match system (currently ${resolved})`
		: `${LABELS[m]} theme`}
						@click=${() => this.select(m)}
					>${ICONS[m]}<span class="label">${LABELS[m]}</span></button>
				`)}
			</div>
		`;
	}
}

if (!customElements.get('studio-theme-toggle')) {
	customElements.define('studio-theme-toggle', StudioThemeToggle);
}
